import { SysIResultados } from '../../../../../types/types.orders';
import { SysIAnalitosValoresRef } from '../../../../../types/types.config';
import { ResultTypeResult } from '../../../../../types/types.analitos';

/**
 * Funciones puras para calcular las clases de estilo de las celdas de
 * resultados (alterado / alarma / absurdo). Se extrajeron de los componentes
 * de celda para poder calcularlas con `useMemo` durante el render — reactivas
 * al valor de la fila — sin necesidad de `useState` + `useEffect` (que provoca
 * un segundo render por celda) ni de ejecutar hooks pesados por celda.
 */

/** Extrae el valor numérico de strings como ">1000" o "<500". */
export function extractNumericValue(value: string): number | null {
  if (!value) return null;
  const cleaned = String(value).trim();
  const numStr = cleaned.replace(/^[><]\s*/, '');
  const num = parseFloat(numStr);
  return isNaN(num) ? null : num;
}

/**
 * Indica si el resultado de la fila está "alterado" (fuera de rango de
 * referencia, igual al valor crítico textual, o dentro de los valores no
 * permitidos). Cada componente mapea el booleano a su propia clase CSS.
 */
export function isResultAltered(rowData?: SysIResultados): boolean {
  if (!rowData) return false;
  const trId = rowData.Analito.An_Tr_Id;

  if (trId === ResultTypeResult.NUMERICO || trId === ResultTypeResult.FORMULA) {
    if (rowData.Res_ValRefMin === null || rowData.Res_ValRefMax === null) return false;
    if (!rowData.Res_Resultado || (!rowData.Res_ValRefMin && !rowData.Res_ValRefMax) || (rowData.Res_ValRefMin === 0 && rowData.Res_ValRefMax === 0)) {
      return false;
    }

    const resultString = String(rowData.Res_Resultado).trim();
    const numericValue = extractNumericValue(resultString);
    if (numericValue === null || isNaN(numericValue)) return false;

    const hasGreaterThan = resultString.startsWith('>');
    const hasLessThan = resultString.startsWith('<');

    if (hasGreaterThan) return numericValue > rowData.Res_ValRefMax;
    if (hasLessThan) return numericValue < rowData.Res_ValRefMin;
    if (numericValue < rowData.Res_ValRefMin) return true;
    if (numericValue > rowData.Res_ValRefMax) return true;
    return false;
  }

  if (
    (trId === ResultTypeResult.TEXTUAL || trId === ResultTypeResult.AUTOCOMPLETAR || trId === ResultTypeResult.LISTA) &&
    rowData.Analito.An_ValCritico &&
    rowData.Analito.An_ValCritico?.toUpperCase().trim() === rowData.Res_Resultado?.toUpperCase().trim()
  ) {
    const hasSpecialChars = /[^\w\s]/.test(rowData.Analito.An_ValCritico);
    return !hasSpecialChars;
  }

  if (
    (trId === ResultTypeResult.TEXTUAL || trId === ResultTypeResult.LISTA || trId === ResultTypeResult.AUTOCOMPLETAR || trId === ResultTypeResult.MULTILINEA) &&
    rowData.Analito.An_ValRefTextPermitidos &&
    rowData.Res_Resultado &&
    rowData.Res_Resultado.trim() !== ''
  ) {
    const noPermitidos = rowData.Analito.An_ValRefTextPermitidos.split(',')
      .map((v: string) => v.trim().toUpperCase())
      .filter((v: string) => v.length > 0);
    if (noPermitidos.includes(rowData.Res_Resultado.toUpperCase().trim())) return true;
  }

  return false;
}

/**
 * Devuelve la clase de estilo de alarma/absurdo para un resultado numérico o
 * textual crítico. Versión "con etiqueta" (clases *-lbl) usada por las celdas
 * de visualización.
 */
export function getAlarmAndAbsurdLabel(rowData: SysIResultados, lstValRefers?: SysIAnalitosValoresRef[]): string {
  const lstValRef = lstValRefers?.find((x) => x.Avr_Sd_Id === rowData?.Orden.Ord_Sd_Id && x.Avr_An_Id === rowData?.Res_An_Id);

  if (rowData?.Analito.An_Tr_Id === ResultTypeResult.NUMERICO || rowData?.Analito.An_Tr_Id === ResultTypeResult.FORMULA) {
    if (!rowData?.Res_Resultado || rowData?.Res_Resultado.includes('||ADJUNTO||')) {
      return '';
    }
    if (lstValRef) {
      const resultString = String(rowData.Res_Resultado).trim();
      const numericValue = extractNumericValue(resultString);
      if (numericValue === null || isNaN(numericValue)) return '';

      const hasGreaterThan = resultString.startsWith('>');
      const hasLessThan = resultString.startsWith('<');

      if (lstValRef.Avr_MinimoAbsurdo !== null && lstValRef.Avr_MaximoAbsurdo !== null) {
        if (lstValRef.Avr_MinimoAbsurdo !== 0 || lstValRef.Avr_MaximoAbsurdo !== 0) {
          if (hasGreaterThan) {
            if (numericValue > lstValRef.Avr_MaximoAbsurdo) return 'result-absurd-lbl';
          } else if (hasLessThan) {
            if (numericValue < lstValRef.Avr_MinimoAbsurdo) return 'result-absurd-lbl';
          } else {
            if (numericValue < lstValRef.Avr_MinimoAbsurdo || numericValue > lstValRef.Avr_MaximoAbsurdo) return 'result-absurd-lbl';
          }
        }
      }
      if (lstValRef.Avr_MinimoAlarma !== null && lstValRef.Avr_MaximoAlarma !== null) {
        if (lstValRef.Avr_MinimoAlarma !== 0 || lstValRef.Avr_MaximoAlarma !== 0) {
          if (hasGreaterThan) {
            if (numericValue > lstValRef.Avr_MaximoAlarma) return 'result-alarm-lbl';
          } else if (hasLessThan) {
            if (numericValue < lstValRef.Avr_MinimoAlarma) return 'result-alarm-lbl';
          } else {
            if (numericValue < lstValRef.Avr_MinimoAlarma || numericValue > lstValRef.Avr_MaximoAlarma) return 'result-alarm-lbl';
          }
        }
      }
    }
  }

  // Resultados textuales con valor crítico
  if (
    (rowData?.Analito.An_Tr_Id === ResultTypeResult.TEXTUAL || rowData?.Analito.An_Tr_Id === ResultTypeResult.AUTOCOMPLETAR || rowData?.Analito.An_Tr_Id === ResultTypeResult.LISTA) &&
    rowData.Analito.An_ValCritico &&
    rowData.Analito.An_ValCritico?.toUpperCase().trim() === rowData.Res_Resultado?.toUpperCase().trim()
  ) {
    const hasSpecialChars = /[^\w\s]/.test(rowData.Analito.An_ValCritico);
    if (!hasSpecialChars) return 'result-absurd-lbl';
  }

  return '';
}

/**
 * Versión booleana "sin etiqueta" de la comprobación de alarma/absurdo, usada
 * en la lógica de validación para excluir resultados críticos.
 */
export function getAlarmAndAbsurdFlag(rowData: SysIResultados, lstValRefers?: SysIAnalitosValoresRef[]): boolean {
  const lstValRef = lstValRefers?.find((x) => x.Avr_Sd_Id === rowData?.Orden.Ord_Sd_Id && x.Avr_An_Id === rowData?.Res_An_Id);

  // Críticos textuales (An_ValCritico) para TEXTUAL, LISTA, AUTOCOMPLETAR
  if (
    (rowData?.Analito.An_Tr_Id === ResultTypeResult.TEXTUAL || rowData?.Analito.An_Tr_Id === ResultTypeResult.LISTA || rowData?.Analito.An_Tr_Id === ResultTypeResult.AUTOCOMPLETAR) &&
    rowData?.Analito.An_ValCritico &&
    rowData?.Res_Resultado &&
    rowData.Analito.An_ValCritico.toUpperCase().trim() === rowData.Res_Resultado.toUpperCase().trim()
  ) {
    const hasSpecialChars = /[^\w\s]/.test(rowData.Analito.An_ValCritico);
    if (!hasSpecialChars) return true;
  }

  if (rowData?.Analito.An_Tr_Id === 1 || rowData?.Analito.An_Tr_Id === 3) {
    if (!rowData?.Res_Resultado) return false;
    if (lstValRef) {
      const resultString = String(rowData.Res_Resultado).trim();
      const numericValue = extractNumericValue(resultString);
      if (numericValue === null || isNaN(numericValue)) return false;

      const hasGreaterThan = resultString.startsWith('>');
      const hasLessThan = resultString.startsWith('<');

      if (lstValRef.Avr_MinimoAbsurdo !== null && lstValRef.Avr_MaximoAbsurdo !== null) {
        if (lstValRef.Avr_MinimoAbsurdo !== 0 || lstValRef.Avr_MaximoAbsurdo !== 0) {
          if (hasGreaterThan) {
            if (numericValue > lstValRef.Avr_MaximoAbsurdo) return true;
          } else if (hasLessThan) {
            if (numericValue < lstValRef.Avr_MinimoAbsurdo) return true;
          } else {
            if (numericValue < lstValRef.Avr_MinimoAbsurdo || numericValue > lstValRef.Avr_MaximoAbsurdo) return true;
          }
        }
      }
      if (lstValRef.Avr_MinimoAlarma !== null && lstValRef.Avr_MaximoAlarma !== null) {
        if (lstValRef.Avr_MinimoAlarma !== 0 || lstValRef.Avr_MaximoAlarma !== 0) {
          if (hasGreaterThan) {
            if (numericValue > lstValRef.Avr_MaximoAlarma) return true;
          } else if (hasLessThan) {
            if (numericValue < lstValRef.Avr_MinimoAlarma) return true;
          } else {
            if (numericValue < lstValRef.Avr_MinimoAlarma || numericValue > lstValRef.Avr_MaximoAlarma) return true;
          }
        }
      }
    }
  }

  return false;
}
